import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Progress,
} from "@/components/ui";
import { FormProvider } from "react-hook-form";
import useSafetyCompliance from "./useSafetyCompliance";
import { CHECKLIST, SAFETY_CHECKLIST } from "./consts";
import { FormSelect, Loader, PhotoSlider, TaskFormContainer } from "@/components";
import { CircleCheck, CircleX } from "lucide-react";
import { cn } from "@/lib/utils";
import { images } from "@/assets";
import { ChecksNoteDialog } from "./ChecksNoteDialog";

const SafetyCompliance = () => {
  const { form, openTabs, onAccordionChange, isLoadingChecklist, safetyChecklist, requiredFields, isAddPending, isUpdatePending, onSubmit,
    isLoadingChecks, isView, isPlanApproved,
    onAttachmentClick, onNotesClick, isPhotoSliderOpen, setIsPhotoSliderOpen, selectedPhotos, isAttachmentsLoading,
    isNotesDialogOpen, setIsNotesDialogOpen } = useSafetyCompliance();
  const {errors} = form.formState;

  return isLoadingChecklist || isLoadingChecks ? <Loader /> : (
    <>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 gap-[20px] pt-[20px] min-h-[400px] overflow-y-scroll px-5 pb-10 no-scrollbar">
            {isView ? <></> : (
            <TaskFormContainer label="Checklist Task">
              <FormSelect
                name={SAFETY_CHECKLIST}
                control={form.control}
                label=""
                placeholder="please select"
                error={errors[SAFETY_CHECKLIST]?.message}
                isRequired={requiredFields.includes(SAFETY_CHECKLIST)}
                options={safetyChecklist?.data?.filter((data: any) => 
                  !Object.values(form.watch(CHECKLIST) || {}).some(
                    (val: any) =>
                      val.taskSafetyChecklistId === data.taskSafetyChecklistId
                  ))
                  ?.map((data: any) => ({label: data?.title, value: data?.taskSafetyChecklistId?.toString()}))}
                multiSelect
                showChip
                disabled={isView}
              />
            </TaskFormContainer>
            )}
            <div className={cn("flex flex-row justify-end gap-3 pb-3 w-full px-5", isView ? "pt-1" : "flex flex-row justify-end gap-3 pb-3 w-full px-5 border-t-[0.5px]")}>
              {/* <Button variant="outline" type="button" onClick={onCancel}>
                Cancel
              </Button> */}
              {isView ? <></> : <><Button
                type="submit"
                value="save"
                disabled={isAddPending || isUpdatePending}
              >
                {isAddPending || isUpdatePending ? <Loader /> : <></>} Save
              </Button>
              </>}
            </div>
            <div>
              <Accordion
                type="multiple"
                value={openTabs}
                onValueChange={onAccordionChange}
              >
                {Object.values(form.watch(CHECKLIST))?.length == 0 ? <div className="flex items-center justify-center">
                  <img src={images.no_data} className="h-[100px]" />
                </div> : Object.values(form.watch(CHECKLIST))?.map((data: any, index: number) => {
                  const progress = Math.round(data?.items?.filter((check: any) => check?.status?.toLowerCase() == "passed")?.length / data?.items?.length * 100) || 0;
                  return (
                    <AccordionItem
                      key={`safety-accordion-${index}`}
                      value={data?.accordionId}
                      className="px-5 rounded-lg mb-[20px] !border-1 border-gray-500"
                    >
                      <AccordionTrigger className="cursor-pointer text-brown-300">
                        {data?.title || ""}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="grid grid-cols-1 gap-2 text-[14px]">
                          <div className="text-gray-500 flex flex-row gap-3 items-center">
                            <span>{data?.items?.length} checks required</span>
                            <div className="flex flex-row items-center gap-1">
                              <div className="h-2 w-2 bg-primary rounded-full" />{" "}
                              Complete all to finish
                            </div>
                          </div>
                          <div className="gap-2 flex flex-col">
                            <span className="text-black font-[600]">Progress</span>
                            <Progress
                              value={(progress || "0") as number}
                              className=" [&>div]:bg-primary bg-gray-200"
                            />
                          </div>
                          <div className="flex flex-col gap-2 text-brown-400 text-[500]">
                            {data?.items?.map((check: any, index: number) => (
                              <div className="flex flex-row gap-3 items-center justify-between">
                                <div className="flex flex-row gap-1 items-center">
                                  <span>{index + 1}.</span>
                                  <span>
                                    <u>{check?.name || ""}</u>
                                  </span>
                                </div>
                                {isPlanApproved ? <div className="flex flex-row gap-2">
                                  <Button
                                    type="button"
                                    variant="link"
                                    className="text-primary underline px-0"
                                    onClick={() => onNotesClick(check)}
                                    disabled={isAttachmentsLoading}
                                  >
                                    Notes
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="link"
                                    className="text-primary underline px-0"
                                    onClick={() => onAttachmentClick(check)}
                                    disabled={isAttachmentsLoading}
                                  >
                                    Attachment
                                  </Button>
                                </div> : <></>}
                                {check.status?.toLowerCase() == "passed" ? (
                                  <CircleCheck className="stroke-green-600" />
                                ) : check.status?.toLowerCase() == "failed" ? (
                                  <CircleX className="stroke-red-600" />
                                ) : (
                                  <div className="h-6 w-6 border-2 border-dashed border-yellow-600 rounded-full px-2" />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  )
                })}
              </Accordion>
            </div>
          </div>
        </form>
      </FormProvider>
      <PhotoSlider
        isOpen={isPhotoSliderOpen}
        onClose={() => setIsPhotoSliderOpen(false)}
        photos={selectedPhotos}
        category="Safety Check Attachments"
        showActions={false}
      />
      <ChecksNoteDialog 
        open={isNotesDialogOpen}
        setOpen={setIsNotesDialogOpen}
      />
    </>
  );
};

export default SafetyCompliance;
